import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import api from "../../utils/axiosConfig";

export const useFaceMatch = (id, user, media) => {
  const [myFacePhotos, setMyFacePhotos] = useState([]);
  const [isMatching, setIsMatching] = useState(false);
  const [matchStartTime, setMatchStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const cacheKey = `faceMatch_${user?._id}_${id}_${user?.selfie || ""}`;

  const resetFaceMatch = useCallback(() => {
    localStorage.removeItem(cacheKey);
    setMyFacePhotos([]);
  }, [cacheKey]);

  // Timer for face matching
  useEffect(() => {
    let timer;
    if (isMatching && matchStartTime) {
      timer = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - matchStartTime) / 1000));
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isMatching, matchStartTime]);

  // Face matching with dynamic LocalStorage cache and unmount/stale protection
  useEffect(() => {
    if (!media.length || !user?._id) return;

    let ignore = false;
    let cachedData = null;

    // 1, 2, 3: Extract valid IDs, sort deterministically, generate stable signature
    const validIds = media.map((m) => m._id).filter(Boolean);
    validIds.sort();
    const currentMediaSignature = validIds.join(",");

    const cachedString = localStorage.getItem(cacheKey);
    if (cachedString) {
      try {
        const parsed = JSON.parse(cachedString);

        // Backward compatibility for old raw arrays or lastLength objects
        if (Array.isArray(parsed)) {
          cachedData = { mediaSignature: "", matches: parsed };
        } else if (parsed.lastLength !== undefined && parsed.mediaSignature === undefined) {
          cachedData = { mediaSignature: "", matches: parsed.matches || [] };
        } else {
          cachedData = parsed;
        }

        // Immediately display cached matches, intersected with current media to drop deleted photos
        if (cachedData && Array.isArray(cachedData.matches)) {
          const mediaIds = new Set(media.map((m) => m._id));
          const validMatches = cachedData.matches.filter((m) => mediaIds.has(m._id));
          setMyFacePhotos(validMatches);
        }
      } catch (error) {
        console.error("Error parsing cached data:", error);
        localStorage.removeItem(cacheKey);
      }
    }

    // Detect if the exact photo set changed
    if (cachedData && cachedData.mediaSignature === currentMediaSignature) {
      return; // Exact same photo set, cache is fully up to date
    }

    const runMatcher = async () => {
      if (!cachedData) {
        toast.info("🔍 Finding your photos...");
      }
      setIsMatching(true);
      setMatchStartTime(Date.now());
      setElapsedTime(0);

      let isDuplicate = false;
      try {
        const res = await api.post("/facematch/run", { groupId: id });

        if (ignore) return;

        if (res.data && res.data.status === "processing") {
          toast.info("New photos are being processed. Your existing matches are still available.", { autoClose: 8000 });
          setIsMatching(false);
          return; // DO NOT clear matches or overwrite cache
        }

        const matchedUrls = new Set(res.data.matchedPhotoUrls || []);
        const matched = media.filter((m) => matchedUrls.has(m.filePath));

        setMyFacePhotos(matched);

        // Remove heavy embeddings from cached items to save 95%+ localStorage space
        try {
          const lightMatched = matched.map(({ embeddings, ...rest }) => rest);
          localStorage.setItem(cacheKey, JSON.stringify({
            mediaSignature: currentMediaSignature,
            matches: lightMatched
          }));
        } catch (e) {
          console.warn("⚠️ Failed to cache matched photos in localStorage (Quota Exceeded):", e.message);
        }

        // Only toast if it's an initial scan or we found genuinely new photos
        if (!cachedData || matched.length !== (cachedData.matches?.length || 0)) {
          toast.success(
            `✨ Found ${matched.length} photo${matched.length !== 1 ? "s" : ""}!`
          );
        }
      } catch (error) {
        if (ignore) return;
        if (error.response && error.response.status === 429) {
          isDuplicate = true;
          console.warn("Duplicate face match request skipped.");
          return;
        }
        console.error("Face matching error:", error);
        toast.error("Face matching failed");
      } finally {
        if (!ignore && !isDuplicate) {
          setIsMatching(false);
        }
      }
    };

    runMatcher();

    return () => {
      ignore = true;
    };
  }, [media, id, cacheKey, user?._id]);

  return {
    myFacePhotos,
    isMatching,
    elapsedTime,
    resetFaceMatch,
  };
};
